import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';

import {
  SurchargeChargeMethod,
  SurchargeConditionChildType,
  SurchargeConditionOperator,
  SurchargeConditionProperty,
  SurchargeConditionPropertyType,
} from '@invenco/shipping-interface';
import type { CarrierDTO, CreateSurchargeDTO } from '@invenco/shipping-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { DatePickerFormField } from '@/components/forms/date-picker-form-field';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { NumberFormField } from '@/components/forms/number-form-field';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import type { Result } from '@/shared/helpers/Result';

import {
  buildApplicability,
  emptyConditionsForm,
  isArrayOperator,
  operatorsForProperty,
  propertyType,
} from './surcharge-applicability';
import { surchargeChargeMethodTitle } from './surcharge-columns';
import { SurchargeConditionsBuilder } from './surcharge-conditions-builder';

// Sentinel for the carrier-wide option (radix Select forbids empty-string item values).
const ALL_SERVICES_VALUE = 'all-services';

/** A single condition row. Shape only — completeness is validated on the parent, gated on `enabled`,
 * so the default empty row of a disabled (dropped) conditions section never blocks submit. */
const conditionRowSchema = z.object({
  property: z.enum(SurchargeConditionProperty),
  operator: z.enum(SurchargeConditionOperator),
  value: z.string(),
  values: z.array(z.string()),
});

/** Validates one row's operator/value against its property type; issues are pathed under the row. */
function validateConditionRow(
  row: z.output<typeof conditionRowSchema>,
  index: number,
  ctx: z.RefinementCtx,
): void {
  if (!operatorsForProperty(row.property).includes(row.operator)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Operator is not valid for this property',
      path: ['rows', index, 'operator'],
    });
    return;
  }
  const isNumber = propertyType(row.property) === SurchargeConditionPropertyType.NUMBER;
  if (isArrayOperator(row.operator)) {
    if (row.values.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Add at least one value',
        path: ['rows', index, 'values'],
      });
      return;
    }
    if (isNumber && row.values.some((value) => !Number.isFinite(Number(value)))) {
      ctx.addIssue({
        code: 'custom',
        message: 'Values must be numbers',
        path: ['rows', index, 'values'],
      });
    }
    return;
  }
  if (row.value.trim() === '') {
    ctx.addIssue({ code: 'custom', message: 'Enter a value', path: ['rows', index, 'value'] });
    return;
  }
  if (isNumber && !Number.isFinite(Number(row.value))) {
    ctx.addIssue({ code: 'custom', message: 'Enter a number', path: ['rows', index, 'value'] });
  }
}

const conditionsSchema = z
  .object({
    enabled: z.boolean(),
    childConditionType: z.enum(SurchargeConditionChildType),
    rows: z.array(conditionRowSchema),
  })
  .superRefine((conditions, ctx) => {
    // Disabled conditions are dropped on submit, so skip all row validation — otherwise the
    // default empty row would block every no-conditions create.
    if (!conditions.enabled) return;
    if (conditions.rows.length === 0) {
      ctx.addIssue({ code: 'custom', message: 'Add at least one condition', path: ['rows'] });
      return;
    }
    conditions.rows.forEach((row, index) => validateConditionRow(row, index, ctx));
  });

const schema = z
  .object({
    name: z.string().min(1, 'Enter a name'),
    appliesTo: z.string().min(1, 'Choose what this surcharge applies to'),
    chargeMethod: z.enum(SurchargeChargeMethod),
    rate: z.number('Enter a rate').positive('Enter a rate greater than zero'),
    validFrom: z.date().optional(),
    conditions: conditionsSchema,
  })
  .refine((data) => data.chargeMethod !== SurchargeChargeMethod.PERCENTAGE || data.rate <= 100, {
    message: 'A percentage rate cannot exceed 100',
    path: ['rate'],
  });

type CreateSurchargeFormValues = z.input<typeof schema>;
type CreateSurchargeSubmitValues = z.output<typeof schema>;

type Props = {
  /** The carrier the surcharge belongs to — its services are the service-scope choices. */
  carrier?: CarrierDTO;
  onSubmit: (surcharge: CreateSurchargeDTO) => Promise<Result> | Promise<void> | void;
};

/**
 * Create-surcharge form, rendered inside a `FormDialog` on the carrier detail page. The rate is
 * entered in human units (percent / dollars) and converted on submit; conditions are an optional
 * structured applicability tree built with {@link SurchargeConditionsBuilder}. The scope is
 * carrier-wide XOR one service — exactly one id is sent.
 */
export function CreateSurchargeDialogContent({ carrier, onSubmit }: Props) {
  const form = useForm<CreateSurchargeFormValues, unknown, CreateSurchargeSubmitValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      appliesTo: ALL_SERVICES_VALUE,
      chargeMethod: SurchargeChargeMethod.PERCENTAGE,
      rate: undefined,
      validFrom: undefined,
      conditions: emptyConditionsForm(),
    },
  });
  const chargeMethod = form.watch('chargeMethod');

  return (
    <FormDialogContent
      form={form}
      submitLabel="Create"
      onSubmit={({ name, appliesTo, chargeMethod: method, rate, validFrom, conditions }) => {
        if (!carrier) return undefined;
        const applicability = buildApplicability(conditions);
        return onSubmit({
          name,
          ...(appliesTo === ALL_SERVICES_VALUE
            ? { carrierId: carrier.id }
            : { carrierServiceId: appliesTo }),
          chargeMethod: method,
          rate: method === SurchargeChargeMethod.PERCENTAGE ? rate / 100 : rate,
          ...(applicability && { applicability }),
          ...(validFrom && { validFrom: validFrom.toISOString() }),
        });
      }}
    >
      <DialogHeader>
        <DialogTitle>New surcharge</DialogTitle>
        <DialogDescription>
          Adds a surcharge for this carrier, applied carrier-wide or to a single service.
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="grid gap-4">
        <TextFormField control={form.control} name="name" label="Name" placeholder="Enter a name" />
        <SelectFormField
          control={form.control}
          name="appliesTo"
          label="Applies to"
          className="w-full"
        >
          <SelectItem value={ALL_SERVICES_VALUE}>All services (carrier-wide)</SelectItem>
          {(carrier?.carrierServices ?? []).map((service) => (
            <SelectItem key={service.id} value={service.id}>
              {service.name || service.code || service.id}
            </SelectItem>
          ))}
        </SelectFormField>
        <div className="grid gap-4 @md:grid-cols-2">
          <SelectFormField
            control={form.control}
            name="chargeMethod"
            label="Charge method"
            className="w-full"
          >
            {Object.entries(surchargeChargeMethodTitle).map(([value, title]) => (
              <SelectItem key={value} value={value}>
                {title}
              </SelectItem>
            ))}
          </SelectFormField>
          <NumberFormField
            control={form.control}
            name="rate"
            label={chargeMethod === SurchargeChargeMethod.PERCENTAGE ? 'Rate (%)' : 'Rate ($)'}
            placeholder={
              chargeMethod === SurchargeChargeMethod.PERCENTAGE ? 'e.g. 19.5' : 'e.g. 75.00'
            }
            min={0}
            step="any"
          />
        </div>
        <DatePickerFormField
          control={form.control}
          name="validFrom"
          label="Effective from"
          description="Defaults to now. Pick a future date to schedule the surcharge."
        />
        <SurchargeConditionsBuilder control={form.control} />
      </DialogBody>
    </FormDialogContent>
  );
}
